// src/components/ProductCard.jsx
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

// helpers
const normalize = (s = '') =>
  s
    .toString()
    .trim()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

const getImageList = (product) => {
  const m = Array.isArray(product?.media) ? product.media : [];
  return [m?.[0]?.url, m?.[1]?.url, m?.[2]?.url].filter(Boolean);
};

// déduplication services (clé = nom de fichier d'icône, sinon titre normalisé)
const getServices = (product) => {
  const raw =
    product?.services ?? product?.info?.services ?? product?.info?.[0]?.services ?? [];

  const seen = new Set();
  const out = [];
  const norm = (s = '') =>
    s
      .toString()
      .trim()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();

  for (const s of raw) {
    const iconFile = (s?.icon || '').split('/').pop()?.toLowerCase();
    const titleNorm = norm(s?.title || '');
    const key = iconFile ? `i:${iconFile}` : `t:${titleNorm}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
};

function ProductCard({ product }) {
  // console.log('%csrc\b2bcomponentsProductCard.jsx:49 product', 'color: #007acc;', product);
  // images + fallback
  const imageList = useMemo(() => getImageList(product), [product]);
  const [imgIdx, setImgIdx] = useState(0);
  const imgSrc = imageList[imgIdx] || '/assets/img/placeholder.jpg';
  const onImgError = () => setImgIdx((i) => (i + 1 < imageList.length ? i + 1 : i));

  // drapeau
  const countryId = product.location?.country?.id?.split('_').pop()?.toLowerCase();
  const flagUrl = countryId ? `https://flagcdn.com/w160/${countryId}.png` : null;

  // best from
  const bestFrom = product?.bestPrice?.bestPrice?._$From ?? product?.bestPrice?._$From;
  const capitalize = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';
  const norm = (x) =>
    String(x ?? '')
      .toUpperCase()
      .replace(/^_?LT_/, '');
  const froms = product?.froms ?? product?.Froms ?? [];
  const matchedFrom = froms.find((f) => norm(f?.id) === norm(bestFrom));

  // texte
  const truncateText = (t, max) => (!t ? '' : t.length > max ? `${t.slice(0, max)}...` : t);
  const capitalizeFirstLetter = (t) => (!t ? '' : t.charAt(0).toUpperCase() + t.slice(1));

  // services dédupliqués + limite d’affichage
  const services = useMemo(() => getServices(product).slice(0, 15), [product]);
  // console.log('%csrc\b2bcomponentsProductCard.jsx:77 services', 'color: #007acc;', services);

  // prix
  const priceCents = product?.bestPrice?.bestPrice?.Price?._$Value ?? null;
  const priceHuman =
    priceCents != null ? `${Math.round(priceCents / 100)} €` : 'Non disponible';

  // route détail + passage de l'objet complet
  const detailHref = `/produit/${product._id}`;

  return (
    <div className="mb-4">
      <Link to={detailHref} state={{ product }}>
        <div className="card-title">
          <h3 style={{ padding: '13px' }} className="text-secondary">
            {truncateText(capitalizeFirstLetter(product.description?.name), 35) || 'Sans nom'}
          </h3>
        </div>

        <div>
          <span
            className="alert alert-primary position-absolute top-1 start-0 m-2"
            style={{ zIndex: 1, padding: '.25rem 1rem' }}
          >
            {capitalizeFirstLetter(product.productType)}
          </span>

          <img
            src={imgSrc}
            onError={onImgError}
            alt={product.description?.name || ''}
            style={{ width: '100%', height: '230px', objectFit: 'cover' }}
            loading="lazy"
          />
        </div>

        {flagUrl && (
          <img
            src={flagUrl}
            className="rounded-circle d-block mx-auto"
            alt="flag"
            width="52"
            height="50"
            style={{ marginTop: '-1.5rem', borderRadius: '50%', objectFit: 'cover' }}
          />
        )}

        <div className="d-flex justify-content-end">
          À partir de{' '}
          <strong>
            <h3 className="text-primary px-1" style={{ marginTop: '-2px' }}>
              {priceHuman}
              <span>/pers</span>
            </h3>
          </strong>
        </div>

        <div className="card-body pr-5 pl-5 pn-5 p-0" style={{ color: '#000' }}>
          <div className="d-flex">
            Ville de départ :{' '}
            <h4 style={{ color: '#000064', marginLeft: 5 }}>
              {matchedFrom ? capitalize(matchedFrom.title) : bestFrom || '—'}
            </h4>
          </div>
          <strong>{product.location?.city?.title || 'Non spécifiée'}</strong> <br />
          Pour <strong>{product.information?.Night} nuitées</strong> <br />
          Le <strong>{product.bestPrice?.startsAt || 'Non disponible'}</strong>
          <br />
          {/* services (dédupliqués) */}
          {services.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mt-3 justify-content-center">
              {services.map((s, idx) => (
                <div
                  key={s.id || s._id || s.title || idx}
                  className="d-flex flex-column align-items-center gap-1 mb-3 col-3 text-center"
                >
                  <div>
                    {s.icon ? (
                      <img
                        height="35"
                        width="auto"
                        src={s.icon.startsWith('/') ? s.icon : s.icon}
                        alt=""
                        title={s.title}
                        loading="lazy"
                      />
                    ) : null}
                    <span className="d-block text-primary" style={{ fontSize: '10px' }}>
                      {s.title}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Link>
    </div>
  );
}

export default ProductCard;
